import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    collectionGroup,
    getDocs,
    query,
    orderBy,
} from 'firebase/firestore';
import type { Timestamp, DocumentData } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './BizBusinessPlans.css';

interface BusinessPlan {
    id: string;
    path: string;
    planName: string;
    description: string;
    createdAt: Timestamp;
    data: DocumentData;
}

export const BizBusinessPlans: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser, signOut } = useAuth();

    const [plans, setPlans] = useState<BusinessPlan[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [selectedPlan, setSelectedPlan] = useState<BusinessPlan | null>(null);

    useEffect(() => {
        loadPlans();
    }, []);

    const loadPlans = async () => {
        try {
            setLoading(true);
            setError('');
            const q = query(
                collectionGroup(db, 'businessPlans'),
                orderBy('createdAt', 'desc')
            );
            const snap = await getDocs(q);
            const loaded: BusinessPlan[] = snap.docs.map((d) => {
                const raw = d.data();
                return {
                    id: d.id,
                    path: d.ref.path,
                    planName: raw.planName ?? '(Untitled)',
                    description: raw.description ?? '',
                    createdAt: raw.createdAt,
                    data: raw,
                };
            });
            setPlans(loaded);
        } catch (err: any) {
            setError(err.message || 'Failed to load business plans');
        } finally {
            setLoading(false);
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut();
            navigate('/login');
        } catch (err) {
            console.error('Sign out failed:', err);
        }
    };

    const formatDate = (ts: Timestamp | undefined) => {
        if (!ts?.toDate) return 'Unknown';
        return ts.toDate().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const filteredPlans = plans.filter((p) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (
            p.planName.toLowerCase().includes(q) ||
            p.description.toLowerCase().includes(q)
        );
    });

    return (
        <div className="biz-plans-container">
            <header className="biz-plans-header">
                <div className="biz-plans-header-content">
                    <div>
                        <h1>📋 Biz Business Plans</h1>
                        <p className="biz-plans-subtitle">Submitted plans from biz.omore.app users</p>
                    </div>
                    <div className="biz-plans-header-actions">
                        <button onClick={() => navigate('/dashboard')} className="biz-plans-back-button">
                            ← Back to Dashboard
                        </button>
                        <span className="biz-plans-user-email">{currentUser?.email}</span>
                        <button onClick={handleSignOut} className="biz-plans-logout-button">
                            Sign Out
                        </button>
                    </div>
                </div>
            </header>

            <main className="biz-plans-main">
                {error && (
                    <div className="biz-plans-error">
                        <span>⚠️ {error}</span>
                        <button onClick={loadPlans} className="biz-plans-retry">Retry</button>
                    </div>
                )}

                <div className="biz-plans-search-row">
                    <input
                        type="text"
                        placeholder="Search by plan name or description..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="biz-plans-search-input"
                    />
                    <span className="biz-plans-count">
                        {filteredPlans.length} of {plans.length} plans
                    </span>
                </div>

                <div className="biz-plans-layout">
                    <div className="biz-plans-list">
                        {loading ? (
                            <div className="biz-plans-loading">
                                <div className="biz-plans-spinner"></div>
                                <p>Loading plans...</p>
                            </div>
                        ) : filteredPlans.length === 0 ? (
                            <div className="biz-plans-empty">
                                <div className="biz-plans-empty-icon">📋</div>
                                <h3>No plans found</h3>
                                <p>{search ? 'Try a different search.' : 'No business plans submitted yet.'}</p>
                            </div>
                        ) : (
                            filteredPlans.map((plan) => (
                                <div
                                    key={plan.path}
                                    className={`biz-plan-card ${selectedPlan?.path === plan.path ? 'selected' : ''}`}
                                    onClick={() => setSelectedPlan(plan)}
                                >
                                    <h3 className="biz-plan-name">{plan.planName}</h3>
                                    <p className="biz-plan-desc">
                                        {plan.description.length > 120
                                            ? `${plan.description.substring(0, 120)}...`
                                            : plan.description || <em>No description</em>}
                                    </p>
                                    <div className="biz-plan-footer">
                                        <span className="biz-plan-date">
                                            {formatDate(plan.createdAt)}
                                        </span>
                                        <span className="biz-plan-path" title={plan.path}>
                                            {plan.path}
                                        </span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {selectedPlan && (
                        <div className="biz-plan-detail">
                            <div className="biz-plan-detail-header">
                                <h2>{selectedPlan.planName}</h2>
                                <button
                                    className="biz-plan-detail-close"
                                    onClick={() => setSelectedPlan(null)}
                                >
                                    ✕
                                </button>
                            </div>
                            <p className="biz-plan-detail-path">
                                <strong>Path:</strong> {selectedPlan.path}
                            </p>
                            <div className="biz-plan-detail-fields">
                                {Object.entries(selectedPlan.data).map(([key, value]) => (
                                    <div key={key} className="biz-plan-field">
                                        <span className="biz-plan-field-key">{key}</span>
                                        <span className="biz-plan-field-value">
                                            {value instanceof Object && value?.toDate
                                                ? (value as Timestamp).toDate().toLocaleString()
                                                : typeof value === 'object'
                                                ? JSON.stringify(value, null, 2)
                                                : String(value ?? '')}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default BizBusinessPlans;
